/**
 * Linked strip charts (handover 7.1.3), drawn with Apache ECharts.
 *
 * Three rules drive the design:
 *
 *   1. Gaps are honest. The backend returns `null` for missing samples and the segment list says
 *      where a line may legitimately be drawn; a break is forced at every segment boundary and
 *      `connectNulls` is off, so a data gap never renders as a straight interpolation across it.
 *
 *   2. The cursor is shared. Hovering any strip moves one cursor line across *all* of them, and
 *      the replay clock's playhead is drawn in every strip as well - so the strips, the event
 *      bands and the globe always read the same instant. Clicking seeks the replay there.
 *
 *   3. The readout never moves. Values appear in a fixed row under each strip's title, in
 *      right-aligned slots of a fixed width with a fixed decimal count. This replaces the live
 *      legend we drew before, whose table column widths tracked the value text: every mouse move
 *      reflowed the legend, so the numbers - and everything laid out below them - jittered while
 *      you were trying to read them. Nothing here changes size as the cursor moves, so nothing
 *      shifts.
 */

import { LineChart, type LineSeriesOption } from 'echarts/charts';
import { GridComponent, type GridComponentOption } from 'echarts/components';
import * as echarts from 'echarts/core';
import type { ComposeOption, ECharts, ElementEvent } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import type { SeriesResponse } from '../api/types';

echarts.use([LineChart, GridComponent, CanvasRenderer]);

type StripOption = ComposeOption<LineSeriesOption | GridComponentOption>;

/** One drawable sample: epoch ms and a value, or `null` to break the line here. */
type Point = [number, number | null];

/**
 * The plot rectangle inside the canvas. Deliberately fixed rather than ECharts'
 * `grid.containLabel`, because the cursor and playhead are DOM overlays: a grid whose position
 * depends on the rendered label widths could not be tracked without querying the chart's internal
 * layout on every mouse move.
 */
const GRID = { left: 52, right: 8, top: 8, bottom: 18 } as const;

/** Shown wherever there is no sample to report. Same width in the fixed slot as any number. */
const NO_VALUE = '—';

/** Cursor-to-sample tolerance used when the response declares no gap threshold to derive one. */
const FALLBACK_TOLERANCE_MS = 2_000;

/** Stroke widths, shared by the initial option and the theme re-apply so they cannot drift. */
const MAIN_STROKE = { width: 1.4 } as const;
const BRIDGE_STROKE = { width: 1, type: 'dashed', opacity: 0.5 } as const;

export interface StripDefinition {
  key: string;
  title: string;
  unit: string;
  /** Dataset to request, and which columns of it to draw. */
  dataset: string;
  columns: {
    name: string;
    label: string;
    colour: string;
    /**
     * Substitute colour for the light theme. The palette is dark-theme-first (styles.css, handover
     * 7.4), which is fine for the saturated hues but not for a near-white trace: set this wherever
     * `colour` would disappear against a light background.
     */
    colourLight?: string;
    scale?: number;
  }[];
  /** Shown unless the operator toggled it; false = opt-in (kept out of the default view). */
  defaultOn?: boolean;
}

/** The default strips, chosen to answer the questions in the handover's design north star. */
export const DEFAULT_STRIPS: StripDefinition[] = [
  {
    key: 'rates',
    title: 'Body rates (inertial)',
    unit: 'deg/s',
    // The inertial rates live in the backup estimator aggregate; est_highres (the `estimator`
    // dataset) publishes only the ORC rates, so pointing this strip there drew nothing, ever.
    dataset: 'estimator_backup',
    columns: [
      { name: 'rate_irc_x', label: 'X', colour: '#ff7f5a', scale: 180 / Math.PI },
      { name: 'rate_irc_y', label: 'Y', colour: '#5ad07f', scale: 180 / Math.PI },
      { name: 'rate_irc_z', label: 'Z', colour: '#5a9bff', scale: 180 / Math.PI },
    ],
  },
  {
    key: 'omega',
    title: 'Spin rate |omega|',
    unit: 'deg/s',
    dataset: 'derived/spin',
    columns: [
      {
        name: 'omega_magnitude',
        label: '|omega|',
        colour: '#f5f7fa',
        colourLight: '#2b3440',
        scale: 180 / Math.PI,
      },
      { name: 'coning_angle_deg', label: 'coning', colour: '#b05aff' },
    ],
  },
  {
    key: 'wheels',
    title: 'Reaction wheel speed',
    unit: 'RPM',
    dataset: 'wheel_speeds',
    columns: [
      { name: 'speed_X', label: 'X', colour: '#ff7f5a', scale: 60 / (2 * Math.PI) },
      { name: 'speed_Y', label: 'Y', colour: '#5ad07f', scale: 60 / (2 * Math.PI) },
      { name: 'speed_Z', label: 'Z', colour: '#5a9bff', scale: 60 / (2 * Math.PI) },
    ],
  },
  {
    key: 'power',
    title: 'Battery',
    unit: 'V / A',
    dataset: 'battery',
    columns: [
      { name: 'vbat', label: 'Vbat', colour: '#ffc857' },
      // `array_current` never existed in the dataset; the battery current does.
      { name: 'ibat', label: 'Ibat', colour: '#5ad0c0' },
    ],
  },
  {
    key: 'health',
    title: 'Estimator health',
    unit: 'deg',
    dataset: 'derived/all',
    columns: [
      { name: 'sun_consistency_deg', label: 'FSS vs ephemeris', colour: '#ffc857' },
      { name: 'nadir_error_deg', label: 'nadir error', colour: '#9aa4b2' },
      { name: 'mag_angle_deg', label: 'mag angle', colour: '#b05aff' },
    ],
  },
  // EPS strips (power_tlm: realtime + wod_power_tlm). Off by default - the panel row gets
  // crowded fast - and toggled from the strips header.
  {
    key: 'eps_buses',
    title: 'EPS bus voltages',
    unit: 'V',
    dataset: 'power_tlm',
    defaultOn: false,
    columns: [
      { name: 'vbat', label: 'Vbat', colour: '#ffc857' },
      { name: 'vpcm3v3', label: '3V3', colour: '#5ad07f' },
      { name: 'vpcm5v', label: '5V', colour: '#5a9bff' },
      { name: 'vpcm12v', label: '12V', colour: '#ff7f5a' },
    ],
  },
  {
    key: 'eps_loads',
    title: 'EPS load currents',
    unit: 'A',
    dataset: 'power_tlm',
    defaultOn: false,
    columns: [
      { name: 'i_obc', label: 'OBC', colour: '#ffc857' },
      { name: 'i_adcs', label: 'ADCS', colour: '#5ad07f' },
      { name: 'i_payload', label: 'payload', colour: '#b05aff' },
      { name: 'ipcm3v3', label: '3V3 bus', colour: '#5ad0c0' },
      { name: 'ipcm5v', label: '5V bus', colour: '#5a9bff' },
      { name: 'ipcm12v', label: '12V bus', colour: '#ff7f5a' },
    ],
  },
  {
    key: 'eps_solar',
    title: 'Solar string currents',
    unit: 'A',
    dataset: 'power_tlm',
    defaultOn: false,
    columns: [
      { name: 'ibcr1', label: 'BCR1', colour: '#ff7f5a' },
      { name: 'ibcr2', label: 'BCR2', colour: '#ffc857' },
      { name: 'ibcr3', label: 'BCR3', colour: '#5ad07f' },
      { name: 'ibcr4', label: 'BCR4', colour: '#5ad0c0' },
      { name: 'ibcr5', label: 'BCR5', colour: '#5a9bff' },
      { name: 'ibcr6', label: 'BCR6', colour: '#b05aff' },
      { name: 'ibcr7', label: 'BCR7', colour: '#9aa4b2' },
    ],
  },
];

export interface StripHostOptions {
  container: HTMLElement;
  onSeek: (epochMs: number) => void;
}

interface Strip {
  definition: StripDefinition;
  chart: ECharts;
  wrapper: HTMLElement;
  /** The ECharts host; overlays are positioned in its coordinate space. */
  canvas: HTMLElement;
  cursorLine: HTMLElement;
  playLine: HTMLElement;
  note: HTMLElement;
  /** One value slot per column of the definition, in the same order. */
  cells: HTMLElement[];
  /** The readout's colour swatches, repainted alongside the lines on a theme change. */
  dots: HTMLElement[];
  observer: ResizeObserver;
  /** Real sample times (epoch ms), used to resolve a cursor to the sample it is nearest. */
  times: number[];
  /** Scaled values per column, aligned with `times`. */
  values: (number | null)[][];
  /** How far a sample may sit from the cursor before the readout calls it "no sample". */
  toleranceMs: number;
  /** Decimal count per column, fixed so a number's width never changes as the cursor moves. */
  decimals: number[];
  /**
   * Sample index last written into the readout, so a 60 fps playhead does not rewrite identical
   * text every frame. `undefined` forces the next render (used when the data is replaced).
   */
  shownIndex: number | null | undefined;
}

export class StripCharts {
  private readonly strips = new Map<string, Strip>();
  private playheadMs: number | null = null;
  /** Cursor time under the pointer, shared by every strip; null when not hovering. */
  private hoverMs: number | null = null;
  private windowRange: [number, number] | null = null;
  private readonly cursorLabel: HTMLElement | null;

  constructor(private readonly options: StripHostOptions) {
    this.cursorLabel = document.getElementById('strips-cursor');
    this.renderCursorLabel();
  }

  /**
   * Pin every strip's x-axis to the requested window. Without this each panel auto-scales to
   * its own data extent, so a battery strip holding six minutes of pass telemetry zooms to
   * those six minutes while the derived strips span hours: the panels stop lining up with each
   * other, with the event bands, and with the replay clock, and a window that is mostly empty
   * *looks* full. A fixed window keeps the gap honest - sparse WOD coverage renders as visible
   * empty space.
   */
  setWindow(startMs: number, stopMs: number): void {
    this.windowRange = [startMs, stopMs];
    for (const strip of this.strips.values()) {
      strip.chart.setOption({ xAxis: { min: startMs, max: stopMs } });
      this.syncOverlays(strip);
    }
  }

  /** Build or update one strip from an API response. */
  update(definition: StripDefinition, response: SeriesResponse | null, error?: string): void {
    const strip = this.strips.get(definition.key) ?? this.create(definition);
    if (!response || response.count === 0) {
      strip.note.textContent = error ?? 'no data in this window';
      strip.note.classList.add('strip-note-warn');
      this.applyData(strip, null);
      return;
    }
    this.applyData(strip, response);
    this.setNote(strip, response);
  }

  // ------------------------------------------------------------------------------ rendering

  private create(definition: StripDefinition): Strip {
    const wrapper = document.createElement('section');
    wrapper.className = 'strip';

    const header = document.createElement('header');
    header.className = 'strip-header';
    const title = document.createElement('h3');
    title.textContent = definition.title;
    const unit = document.createElement('span');
    unit.className = 'strip-unit';
    unit.textContent = definition.unit;
    header.append(title, unit);

    const readout = document.createElement('div');
    readout.className = 'strip-readout';
    const cells: HTMLElement[] = [];
    const dots: HTMLElement[] = [];
    for (const column of definition.columns) {
      const slot = document.createElement('span');
      slot.className = 'strip-val';
      const dot = document.createElement('i');
      dot.className = 'strip-dot';
      dot.style.background = colourOf(column);
      dots.push(dot);
      const key = document.createElement('span');
      key.className = 'strip-key';
      key.textContent = column.label;
      const value = document.createElement('span');
      value.className = 'strip-num';
      value.textContent = NO_VALUE;
      slot.append(dot, key, value);
      readout.appendChild(slot);
      cells.push(value);
    }

    const plot = document.createElement('div');
    plot.className = 'strip-plot';
    const canvas = document.createElement('div');
    canvas.className = 'strip-canvas';
    const cursorLine = document.createElement('span');
    cursorLine.className = 'strip-cursor';
    cursorLine.hidden = true;
    const playLine = document.createElement('span');
    playLine.className = 'strip-playhead';
    playLine.hidden = true;
    plot.append(canvas, cursorLine, playLine);

    const note = document.createElement('p');
    note.className = 'strip-note';

    wrapper.append(header, readout, plot, note);
    this.options.container.appendChild(wrapper);

    const chart = echarts.init(canvas, undefined, { renderer: 'canvas' });
    chart.setOption(baseOption(definition, this.windowRange));

    const strip: Strip = {
      definition,
      chart,
      wrapper,
      canvas,
      cursorLine,
      playLine,
      note,
      cells,
      dots,
      observer: new ResizeObserver(() => {
        chart.resize();
        this.syncOverlays(strip);
      }),
      times: [],
      values: definition.columns.map(() => []),
      toleranceMs: FALLBACK_TOLERANCE_MS,
      decimals: definition.columns.map(() => 3),
      shownIndex: undefined,
    };

    // The zrender handler sees every pointer move over the canvas regardless of what is drawn
    // under it, which is what a shared cursor needs - ECharts' own tooltip is off entirely.
    const zr = chart.getZr();
    zr.on('mousemove', (event: ElementEvent) => this.setHover(strip, event.offsetX));
    zr.on('globalout', () => this.setHover(null, 0));
    zr.on('click', (event: ElementEvent) => {
      const time = this.timeAt(strip, event.offsetX);
      if (time !== null) this.options.onSeek(time);
    });
    strip.observer.observe(canvas);

    this.strips.set(definition.key, strip);
    return strip;
  }

  /** Swap in a response's samples, or clear the strip when there are none. */
  private applyData(strip: Strip, response: SeriesResponse | null): void {
    const { definition } = strip;
    const times = response ? response.times.map((t) => Date.parse(t)) : [];
    const values = definition.columns.map((column) => {
      const raw = response?.series[column.name];
      if (!raw) return new Array<number | null>(times.length).fill(null);
      const factor = column.scale ?? 1;
      return raw.map((v) => (v === null ? null : v * factor));
    });

    // `stop` is a half-open end index; a boundary sits before each later segment's first sample.
    const breakBefore = new Set((response?.segments ?? []).slice(1).map((s) => s.start));
    const series: LineSeriesOption[] = [];
    for (const column of values) {
      const path = buildPath(times, column, breakBefore);
      series.push({ data: path }, { data: buildBridges(path) });
    }
    strip.chart.setOption({ series });

    strip.times = times;
    strip.values = values;
    strip.decimals = values.map(decimalsFor);
    // Half the gap threshold, not the whole of it. A gap exists where two samples sit more than
    // the threshold apart, so a tolerance of half it is the widest one that still leaves a dead
    // zone in the middle of *every* real gap - and that dead zone is what makes the readout admit
    // it has no sample instead of quietly reporting the value from the near edge of the hole.
    const gapMs = (response?.gap_threshold_s ?? 0) * 1000;
    strip.toleranceMs = gapMs > 0 ? gapMs / 2 : FALLBACK_TOLERANCE_MS;
    strip.shownIndex = undefined;
    this.renderReadout(strip);
    this.syncOverlays(strip);
  }

  private setNote(strip: Strip, response: SeriesResponse): void {
    strip.note.classList.remove('strip-note-warn');
    const bits: string[] = [];
    if (response.source) bits.push(response.source);
    if (response.count) bits.push(`${response.count} pts`);
    const gapCount = Math.max(response.segments.length - 1, 0);
    if (gapCount > 0) bits.push(`${gapCount} gap${gapCount === 1 ? '' : 's'}`);
    bits.push(...response.notes);
    strip.note.textContent = bits.join(' · ');
    if (gapCount > 0) strip.note.classList.add('strip-note-warn');
  }

  // -------------------------------------------------------------------------- cursor, playhead

  setPlayhead(epochMs: number): void {
    this.playheadMs = epochMs;
    for (const strip of this.strips.values()) {
      this.position(strip, strip.playLine, epochMs);
      // While hovering, the readout follows the pointer instead; the line still moves.
      if (this.hoverMs === null) this.renderReadout(strip);
    }
    if (this.hoverMs === null) this.renderCursorLabel();
  }

  /** Move the shared cursor. `strip` is the one under the pointer, or null when it left. */
  private setHover(strip: Strip | null, offsetX: number): void {
    const next = strip === null ? null : this.timeAt(strip, offsetX);
    if (next === this.hoverMs) return;
    this.hoverMs = next;
    for (const other of this.strips.values()) {
      this.position(other, other.cursorLine, next);
      this.renderReadout(other);
    }
    this.renderCursorLabel();
  }

  /** The time under a pixel, or null when the pixel is outside the plot rectangle. */
  private timeAt(strip: Strip, offsetX: number): number | null {
    const width = strip.canvas.clientWidth;
    if (width <= GRID.left + GRID.right) return null;
    if (offsetX < GRID.left || offsetX > width - GRID.right) return null;
    const converted = strip.chart.convertFromPixel({ gridIndex: 0 }, [offsetX, 0]);
    const time = Array.isArray(converted) ? converted[0] : converted;
    return typeof time === 'number' && Number.isFinite(time) ? time : null;
  }

  /** Place one overlay line at a time, hiding it when that time falls outside the plot. */
  private position(strip: Strip, line: HTMLElement, timeMs: number | null): void {
    const width = strip.canvas.clientWidth;
    const height = strip.canvas.clientHeight;
    if (timeMs === null || width <= GRID.left + GRID.right) {
      line.hidden = true;
      return;
    }
    const x = strip.chart.convertToPixel({ gridIndex: 0 }, [timeMs, 0]);
    const px = Array.isArray(x) ? x[0] : x;
    if (typeof px !== 'number' || !Number.isFinite(px)) {
      line.hidden = true;
      return;
    }
    if (px < GRID.left - 0.5 || px > width - GRID.right + 0.5) {
      line.hidden = true;
      return;
    }
    line.style.top = `${GRID.top}px`;
    line.style.height = `${Math.max(height - GRID.top - GRID.bottom, 0)}px`;
    line.style.transform = `translateX(${px}px)`;
    line.hidden = false;
  }

  private syncOverlays(strip: Strip): void {
    this.position(strip, strip.cursorLine, this.hoverMs);
    this.position(strip, strip.playLine, this.playheadMs);
  }

  /**
   * Write the values at the shared cursor (or, when not hovering, at the playhead) into one
   * strip's slots. Only the text inside the fixed-width slots changes, so the row cannot reflow.
   */
  private renderReadout(strip: Strip): void {
    const time = this.hoverMs ?? this.playheadMs;
    const index = time === null ? null : nearestIndex(strip.times, time, strip.toleranceMs);
    if (index === strip.shownIndex) return;
    strip.shownIndex = index;
    for (let c = 0; c < strip.cells.length; c++) {
      const value = index === null ? null : (strip.values[c]?.[index] ?? null);
      strip.cells[c]!.textContent =
        value === null ? NO_VALUE : value.toFixed(strip.decimals[c] ?? 3);
    }
  }

  private renderCursorLabel(): void {
    if (!this.cursorLabel) return;
    const time = this.hoverMs ?? this.playheadMs;
    this.cursorLabel.textContent = time === null ? NO_VALUE : formatUtc(time);
    this.cursorLabel.dataset.mode = this.hoverMs === null ? 'playhead' : 'hover';
    this.cursorLabel.title =
      this.hoverMs === null ? 'Replay time' : 'Cursor time — click a strip to seek here';
  }

  // ---------------------------------------------------------------------------- lifecycle

  /**
   * Repaint after the operator switches theme. Only the canvas needs this: everything the strips
   * draw in DOM (readout, cursor, playhead, chrome) is styled from CSS variables and has already
   * followed. Cheap enough to run on the click - no data is touched, only the option.
   */
  setTheme(): void {
    const themed = axes(this.windowRange);
    for (const strip of this.strips.values()) {
      const series: LineSeriesOption[] = [];
      strip.definition.columns.forEach((column, c) => {
        const colour = colourOf(column);
        series.push(
          { lineStyle: { ...MAIN_STROKE, color: colour } },
          { lineStyle: { ...BRIDGE_STROKE, color: colour } },
        );
        const dot = strip.dots[c];
        if (dot) dot.style.background = colour;
      });
      strip.chart.setOption({ ...themed, series });
    }
  }

  resize(): void {
    for (const strip of this.strips.values()) {
      strip.chart.resize();
      this.syncOverlays(strip);
    }
  }

  /** Destroy one strip (used when the operator toggles it off). */
  remove(key: string): void {
    const strip = this.strips.get(key);
    if (!strip) return;
    strip.observer.disconnect();
    strip.chart.dispose();
    strip.wrapper.remove();
    this.strips.delete(key);
  }

  clear(): void {
    for (const key of [...this.strips.keys()]) this.remove(key);
  }
}

// ------------------------------------------------------------------------------- chart option

/**
 * Everything about a strip that never changes with the data: geometry, axes, and one line series
 * per column plus its dashed gap-bridge companion.
 */
function baseOption(
  definition: StripDefinition,
  windowRange: [number, number] | null,
): StripOption {
  const series: LineSeriesOption[] = [];
  for (const column of definition.columns) {
    const colour = colourOf(column);
    series.push(
      {
        type: 'line',
        name: column.label,
        data: [],
        symbol: 'none',
        // `null` in the data breaks the path: gaps stay gaps (handover 7.2).
        connectNulls: false,
        lineStyle: { ...MAIN_STROKE, color: colour },
        z: 3,
        silent: true,
        emphasis: { disabled: true },
        animation: false,
      },
      {
        // The dashed bridge across a no-data stretch: it says the series continues without
        // pretending to know how it got there. Solid means data; dotted means nothing was
        // measured here - exactly the distinction a zero-filled or interpolated line destroys.
        type: 'line',
        name: `${column.label} (gap)`,
        data: [],
        symbol: 'none',
        connectNulls: false,
        lineStyle: { ...BRIDGE_STROKE, color: colour },
        z: 2,
        silent: true,
        emphasis: { disabled: true },
        animation: false,
      },
    );
  }

  return {
    // Every time the UI shows or accepts is UTC (handover 4.1), axis ticks included.
    useUTC: true,
    animation: false,
    grid: { ...GRID },
    ...axes(windowRange),
    series,
  };
}

/**
 * The axes, including every colour they take from the stylesheet. Kept in one function because the
 * theme toggle re-applies exactly this: the readout, cursor and playhead are DOM and follow their
 * CSS variables on their own, but the axes are painted into a canvas and have to be repainted.
 * Re-applying the window bounds along with the colours is deliberate - `windowRange` is the source
 * of truth for them, so a theme change cannot knock a strip off the shared window.
 */
function axes(windowRange: [number, number] | null): Pick<StripOption, 'xAxis' | 'yAxis'> {
  const line = cssVar('--border', '#1e2733');
  const label = cssVar('--text-dim', '#7d8899');
  return {
    xAxis: {
      type: 'time',
      min: windowRange?.[0],
      max: windowRange?.[1],
      axisLine: { lineStyle: { color: line } },
      axisTick: { lineStyle: { color: line } },
      // hideOverlap drops colliding ticks instead of letting them overprint, which is what made
      // second-level labels unreadable on short windows before.
      axisLabel: { color: label, fontSize: 10, hideOverlap: true },
      splitLine: { show: true, lineStyle: { color: line } },
    },
    yAxis: {
      type: 'value',
      // Voltages sit near 8 V and wheel speeds go negative: a forced zero baseline would flatten
      // every strip that matters.
      scale: true,
      splitNumber: 3,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: label, fontSize: 10, hideOverlap: true },
      splitLine: { show: true, lineStyle: { color: line } },
    },
  };
}

/** The colour a column draws in under the active theme. */
function colourOf(column: StripDefinition['columns'][number]): string {
  const light = document.documentElement.dataset.theme === 'light';
  return light && column.colourLight ? column.colourLight : column.colour;
}

// ------------------------------------------------------------------------------------ helpers

/**
 * Turn one column into a drawable path, breaking it wherever the backend says the samples are too
 * far apart to join.
 *
 * The API's `segments` say where consecutive samples are close enough to join with a line
 * (docs/FRAMES.md 7). Without the break, a solid stroke runs from the last sample of one segment
 * straight to the first of the next - across hours of nothing - which reads as continuous data.
 */
function buildPath(
  times: number[],
  values: (number | null)[],
  breakBefore: Set<number>,
): Point[] {
  const path: Point[] = [];
  for (let i = 0; i < times.length; i++) {
    if (i > 0 && breakBefore.has(i)) path.push([(times[i - 1]! + times[i]!) / 2, null]);
    path.push([times[i]!, values[i] ?? null]);
  }
  return path;
}

/**
 * The dashed bridges for one path: for every run of nulls between two real samples, a segment
 * from the last point before the hole to the first point after it. A trailing null keeps
 * consecutive bridges from joining up into one long stroke.
 */
function buildBridges(path: Point[]): Point[] {
  const bridges: Point[] = [];
  let previous: Point | null = null;
  let inHole = false;
  for (const point of path) {
    if (point[1] === null) {
      inHole = previous !== null;
      continue;
    }
    if (inHole && previous) bridges.push(previous, point, [point[0], null]);
    previous = point;
    inHole = false;
  }
  return bridges;
}

/**
 * Index of the sample nearest `time`, or null when the nearest one sits further away than
 * `toleranceMs`. Over a gap the readout has to say "no sample" rather than quietly repeat the
 * value from the far side of it.
 */
function nearestIndex(times: number[], time: number, toleranceMs: number): number | null {
  if (times.length === 0) return null;
  let low = 0;
  let high = times.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (times[mid]! < time) low = mid + 1;
    else high = mid;
  }
  // `low` is the first sample at or after `time`; its predecessor may still be closer.
  const before = low > 0 ? low - 1 : low;
  const best = Math.abs(times[low]! - time) <= Math.abs(times[before]! - time) ? low : before;
  return Math.abs(times[best]! - time) <= toleranceMs ? best : null;
}

/**
 * A decimal count for one column, picked from that column's own magnitude. Formatting each value
 * to its own "natural" precision is what makes a live readout twitch; a count fixed per column
 * (inside a fixed-width slot) keeps every number the same shape however the cursor moves.
 *
 * Per column rather than per strip, because a strip's columns need not share a magnitude: pairing
 * |omega| (fractions of a deg/s) with the coning angle (up to 180 deg) under one count rounded the
 * spin rate to a useless `0.0`.
 */
function decimalsFor(column: (number | null)[]): number {
  let peak = 0;
  for (const value of column) {
    if (value !== null && Number.isFinite(value)) peak = Math.max(peak, Math.abs(value));
  }
  if (peak >= 1000) return 0;
  if (peak >= 100) return 1;
  if (peak >= 10) return 2;
  return 3;
}

/** `YYYY-MM-DD HH:MM:SS UTC` - the app shows UTC everywhere (handover 4.1). */
function formatUtc(epochMs: number): string {
  return new Date(epochMs).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
}

/** Read a theme colour from the stylesheet so the charts follow the app's palette. */
function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}
